import type { ReportDataConfig } from '@reports/domain/types';

/**
 * Column definition without the key (used in Map values)
 *
 * The key is stored in the Map key itself to avoid duplication.
 *
 * @example
 * const columnDef: ColumnDefinition = {
 *   label: 'Total de ventas',
 *   format: {
 *     type: ReportColumnFormat.CURRENCY,
 *     options: { decimals: 2, prefix: '$' }
 *   },
 *   width: '100px',
 *   align: 'right',
 *   bold: true,
 * };
 */
export type ColumnDefinition = Readonly<{
  label: string;
  format: ReportDataConfig;
  width?: string;
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
}>;

/**
 * Full column configuration including key (used for input/configuration)
 *
 * This type is used when defining columns in configuration arrays.
 * When stored in a Map, the key is extracted and only ColumnDefinition is stored.
 *
 * @example
 * const columnConfig: ColumnConfig = {
 *   key: 'total_sales',
 *   label: 'Total de ventas',
 *   format: {
 *     type: ReportColumnFormat.CURRENCY,
 *     options: { decimals: 2, prefix: '$' }
 *   },
 *   width: '100px',
 *   align: 'right',
 *   bold: true,
 * };
 */
export type ColumnConfig = Readonly<{
  key: string;
}> &
  ColumnDefinition;

/**
 * Validate columns config are valid
 *
 * @param columnsConfig - The columns config to validate
 * @throws {Error} If the columns config is invalid
 */
export const validateColumnConfig = (columnsConfig: ColumnConfig[]): void => {
  if (!columnsConfig || columnsConfig.length === 0) {
    throw new Error('Columns config is required');
  }

  for (const column of columnsConfig) {
    if (!column?.key?.trim().length) {
      throw new Error('Column key is required');
    }
    if (!column?.label?.trim().length) {
      throw new Error('Column label is required');
    }
    if (!column?.format?.type) {
      throw new Error('Column format is required');
    }
  }
};

/**
 * Creates a ReadonlyMap from an array of column configurations
 *
 * This utility function converts a columnsConfig array into a Map keyed by column key,
 * enabling efficient column lookups during table rendering. The Map preserves insertion
 * order, which is used for column rendering order.
 *
 * The key is extracted from the ColumnConfig and stored as the Map key,
 * while only the ColumnDefinition (without key) is stored as the Map value
 * to avoid duplication.
 *
 * @param {ColumnConfig[]} columnsConfig - Array of column configurations with keys
 * @returns {ReadonlyMap<string, ColumnDefinition>} Immutable Map of column keys to definitions
 *
 * @example
 * const columnsConfig = [
 *   { key: 'name', label: 'Name', format: { type: 'text' } },
 *   { key: 'price', label: 'Price', format: { type: 'currency' } }
 * ];
 * const columnMap = createColumnConfigMap(columnsConfig);
 * // Map structure: ['name', { label: 'Name', format: { type: 'text' } }]
 * const nameColumn = columnMap.get('name');
 */
export const createColumnConfigMap = (columnsConfig: ColumnConfig[]): ReadonlyMap<string, ColumnDefinition> => {
  const map = new Map<string, ColumnDefinition>();

  columnsConfig.forEach((column) => {
    const { key, ...columnDefinition } = column;
    map.set(key, columnDefinition);
  });

  return map;
};
